import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Play } from "lucide-react";

const DURATION = 30;
const COLORS = ["#00F0FF", "#FF003C", "#39FF14", "#EFFF04", "#a855f7"];

export function BubblePop({ onScore }) {
  const [bubbles, setBubbles] = useState([]);
  const [score, setScore] = useState(0);
  const [time, setTime] = useState(DURATION);
  const [running, setRunning] = useState(false);
  const [over, setOver] = useState(false);
  const reported = useRef(false);
  const idRef = useRef(0);

  const start = () => { setBubbles([]); setScore(0); setTime(DURATION); setRunning(true); setOver(false); reported.current = false; };

  useEffect(() => {
    if (!running) return;
    if (time <= 0) { setRunning(false); setOver(true); setBubbles([]); return; }
    const t = setTimeout(() => setTime((x) => x - 1), 1000);
    return () => clearTimeout(t);
  }, [running, time]);

  useEffect(() => {
    if (!running) return;
    const spawn = setInterval(() => {
      setBubbles((b) => [...b.slice(-8), {
        id: idRef.current++, x: 8 + Math.random() * 78, y: 8 + Math.random() * 78,
        size: 34 + Math.random() * 30, color: COLORS[Math.floor(Math.random() * COLORS.length)],
      }]);
    }, 650);
    return () => clearInterval(spawn);
  }, [running]);

  const pop = (id) => { setBubbles((b) => b.filter((x) => x.id !== id)); setScore((s) => s + 1); };

  useEffect(() => { if (over && !reported.current) { reported.current = true; onScore?.(score * 25); } }, [over, score, onScore]);

  return (
    <div className="flex flex-col items-center w-full" data-testid="bubble-pop">
      <div className="flex items-center justify-between w-full max-w-md mb-3 font-mono">
        <span className="text-neon-cyan text-lg" data-testid="bubble-score">POPPED: {score}</span>
        <span className={`text-lg ${time <= 10 ? "text-neon-pink animate-blink" : "text-neon-yellow"}`}>⏱ {time}s</span>
      </div>
      <div className="relative bg-zinc-950 border-2 border-zinc-700 rounded-xl overflow-hidden w-full max-w-md" style={{ aspectRatio: "1" }}>
        {bubbles.map((b) => (
          <button key={b.id} onClick={() => pop(b.id)} data-testid="bubble"
            className="absolute rounded-full transition-transform hover:scale-110 active:scale-90"
            style={{ left: `${b.x}%`, top: `${b.y}%`, width: b.size, height: b.size, background: b.color, boxShadow: `0 0 14px ${b.color}` }} />
        ))}
        {!running && (
          <div className="absolute inset-0 bg-black/85 flex flex-col items-center justify-center gap-3">
            {over && <p className="font-display font-black text-2xl text-neon-lime uppercase" data-testid="bubble-over">Time! {score} popped</p>}
            <Button onClick={start} data-testid="bubble-start" className="bg-neon-cyan text-black rounded-none font-bold uppercase">
              <Play className="w-4 h-4 mr-1 fill-black" /> {over ? "Play Again" : "Start"}
            </Button>
          </div>
        )}
      </div>
      <p className="text-zinc-500 text-xs font-mono mt-3">Pop as many bubbles as you can in 30 seconds!</p>
    </div>
  );
}
